import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import styles from '../../style.module.css'
import { usePricingStore } from '../../pricingStore'
import { fetchMarketPricing } from '../../pricingApi'

const empty = {
	currency: '',
	licensePrice: '',
	compensation: '',
	furtherDamages: '',
	administrativeFee: '',
	manipulatedFee: '',
	ceaseAndDesistFee: '',
	reminderOneFee: '',
	reminderTwoFee: '',
	finalWarningFee: '',
}

// Administrative Fee is read-only in the UI; the value is still round-tripped
// in the pricing payload so the DTO stays backwards compatible.
export const lockedInputStyle = {
	background: '#f1f1f1',
	color: '#666',
	cursor: 'not-allowed',
}

const MarketMasterEditor = ({ market, history }) => {
	const [form, setForm] = useState(empty)
	const [status, setStatus] = useState('idle')
	const {
		activePricingType,
		refreshMarket,
		upsertActiveMarketPricing,
		markets,
	} = usePricingStore()

	useEffect(() => {
		if (!market) return
		setStatus('loading')
		const local = markets.find((m) => m.market === market)
		if (local) {
			setForm({
				currency: local.currency || '',
				licensePrice: local.licensePrice != null ? local.licensePrice : '',
				compensation: local.compensation != null ? local.compensation : '',
				furtherDamages:
					local.furtherDamages != null ? local.furtherDamages : '',
				administrativeFee:
					local.administrativeFee != null ? local.administrativeFee : '',
				manipulatedFee:
					local.manipulatedFee != null ? local.manipulatedFee : '',
				ceaseAndDesistFee:
					local.ceaseAndDesistFee != null ? local.ceaseAndDesistFee : '',
				reminderOneFee:
					local.reminderOneFee != null ? local.reminderOneFee : '',
				reminderTwoFee:
					local.reminderTwoFee != null ? local.reminderTwoFee : '',
				finalWarningFee:
					local.finalWarningFee != null ? local.finalWarningFee : '',
			})
		} else {
			setForm(empty)
		}
		fetchMarketPricing(market, activePricingType)
			.then((data) => {
				if (data) {
					setForm({
						currency: data.currency || '',
						licensePrice: data.licensePrice != null ? data.licensePrice : '',
						compensation: data.compensation != null ? data.compensation : '',
						furtherDamages:
							data.furtherDamages != null ? data.furtherDamages : '',
						administrativeFee:
							data.administrativeFee != null ? data.administrativeFee : '',
						manipulatedFee:
							data.manipulatedFee != null ? data.manipulatedFee : '',
						ceaseAndDesistFee:
							data.ceaseAndDesistFee != null ? data.ceaseAndDesistFee : '',
						reminderOneFee:
							data.reminderOneFee != null ? data.reminderOneFee : '',
						reminderTwoFee:
							data.reminderTwoFee != null ? data.reminderTwoFee : '',
						finalWarningFee:
							data.finalWarningFee != null ? data.finalWarningFee : '',
					})
					return
				}
				console.log('log')
				// Redirect back to base pricing page if no data for this market/type
				history.replace('/pricing/markets')
			})
			.finally(() => setStatus('idle'))
	}, [market, activePricingType, markets])

	const update = (field, value) => setForm((f) => ({ ...f, [field]: value }))

	const save = async () => {
		if (!market) return
		setStatus('saving')
		try {
			await upsertActiveMarketPricing(market, form)
			await refreshMarket(market)
			setStatus('saved')
			setTimeout(() => setStatus('idle'), 2000)
		} catch (e) {
			console.error(e)
			setStatus('error')
		}
	}

	return (
		<div style={{ flex: 1 }}>
			<h3 style={{ marginTop: 0 }}>
				Market Master Pricing ({activePricingType})
			</h3>
			{!market && <div>Select a market from the left list.</div>}
			{market && (
				<div>
					<h4 style={{ marginTop: 0 }}>Market: {market}</h4>
					<div className={styles.formGroup}>
						<label>Currency *</label>
						<input
							value={form.currency}
							onChange={(e) => update('currency', e.target.value.toUpperCase())}
							placeholder='e.g. EUR'
							maxLength={3}
						/>
					</div>
					<div className={styles.formGroup}>
						<label>License Price *</label>
						<input
							value={form.licensePrice}
							onChange={(e) => update('licensePrice', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Compensation</label>
						<input
							value={form.compensation}
							onChange={(e) => update('compensation', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Further Damages</label>
						<input
							value={form.furtherDamages}
							onChange={(e) => update('furtherDamages', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Administrative Fee (locked)</label>
						<input
							value={form.administrativeFee}
							readOnly
							title='Administrative Fee cannot be edited'
							style={lockedInputStyle}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Manipulated Fee</label>
						<input
							value={form.manipulatedFee}
							onChange={(e) => update('manipulatedFee', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Cease & Desist Fee</label>
						<input
							value={form.ceaseAndDesistFee}
							onChange={(e) => update('ceaseAndDesistFee', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Reminder One Fee</label>
						<input
							value={form.reminderOneFee}
							onChange={(e) => update('reminderOneFee', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.formGroup}>
						<label>Final Warning Fee</label>
						<input
							value={form.finalWarningFee}
							onChange={(e) => update('finalWarningFee', e.target.value)}
							type='number'
							min='0'
							step='0.01'
						/>
					</div>
					<div className={styles.actions}>
						<button
							className={styles.saveButton}
							onClick={save}
							disabled={status === 'saving'}
						>
							Save
						</button>
						{status === 'saving' && <span>Saving...</span>}
						{status === 'saved' && (
							<span style={{ color: 'green' }}>Saved!</span>
						)}
						{status === 'error' && <span style={{ color: 'red' }}>Error</span>}
					</div>
				</div>
			)}
		</div>
	)
}

export default withRouter(MarketMasterEditor)
